import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { runTakeoffJob } from "@/budget/lib/aiTakeoff";

// Accepted MIME types + extensions (kept in sync with backend ai_takeoff.extract_text)
const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.txt,.csv,.tsv,.md";
const MAX_MB = 15;

export default function AITakeoffButton() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const reset = () => {
    setFile(null);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const onSubmit = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const data = await runTakeoffJob(fd);
      const meta = data.ai_meta || {};
      toast.success(
        `AI parsed ${meta.scopes_extracted || 0} scope${meta.scopes_extracted === 1 ? "" : "s"} · ${meta.line_items_extracted || 0} line items`,
      );
      setOpen(false);
      reset();
      // Hand the extracted payload to the Budget Builder via router state.
      // Additional files can be uploaded from within the Builder via the
      // "Add another takeoff" button, which merges into the current budget.
      navigate("/budgets/new", { state: { aiTakeoff: data } });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI takeoff failed";
      toast.error(String(msg).slice(0, 240));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        className="h-7 gap-1 px-2.5 text-xs bg-[#007BFF] hover:bg-[#0069d9] text-white"
        title="Upload a takeoff to start a budget"
        data-testid="ai-takeoff-btn"
      >
        <Upload className="h-3.5 w-3.5" />
        Takeoff
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) reset(); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="ai-takeoff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-[#303E41]" />
              Upload a Takeoff
            </DialogTitle>
            <DialogDescription>
              Upload a takeoff (PDF, DOCX, XLSX, or TXT) and AI will pre-fill the Budget Builder with detected scopes and quantities. Have multiple files for the same project? Upload the first one here — you can add more from inside the Builder to merge them into one budget.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 min-w-0 overflow-hidden">
            {!file ? (
              <label
                htmlFor="ai-takeoff-file"
                className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-sm p-8 cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition"
                data-testid="ai-takeoff-drop"
              >
                <Upload className="w-8 h-8 text-slate-400 mb-2" />
                <div className="text-sm font-medium text-slate-700">Click to upload a takeoff</div>
                <div className="text-xs text-slate-500 mt-1">PDF, DOCX, XLSX, TXT · up to {MAX_MB} MB</div>
                <input
                  id="ai-takeoff-file"
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={onPick}
                  className="sr-only"
                  data-testid="ai-takeoff-file-input"
                />
              </label>
            ) : (
              <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
                <div className="flex flex-1 items-center gap-2 min-w-0">
                  <FileText className="w-5 h-5 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate" data-testid="ai-takeoff-filename">{file.name}</div>
                    <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB · project name will default to the file name</div>
                  </div>
                </div>
                <button
                  onClick={reset}
                  disabled={busy}
                  className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-40"
                  data-testid="ai-takeoff-clear"
                  aria-label="Clear file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="mt-3 text-xs text-slate-500 leading-relaxed">
              Powered by Claude Sonnet 4.6. Parsing usually takes 10–30 seconds. Nothing is saved until you confirm in the Budget Builder.
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); reset(); }}
              disabled={busy}
              className="rounded-sm"
              data-testid="ai-takeoff-cancel"
            >
              Cancel
            </Button>
            <Button variant="data"
              onClick={onSubmit}
              disabled={!file || busy}
              className="rounded-sm gap-2"
              data-testid="ai-takeoff-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</> : <><Upload className="w-4 h-4" /> Parse & Pre-fill</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
